import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable, makeStateKey, TransferState } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { PlatformService } from 'ngx-unificator/services';

@Injectable({
  providedIn: 'root',
})
export class CustomTransferCacheInterceptor implements HttpInterceptor {
  private _transferState = inject(TransferState);
  private _platform = inject(PlatformService);

  private _allowedUrls = [
    '/currency/list/ss',
    '/country/list',
    '/localization/list',
    '/static-content/item',
    '/game/provider/list',
    '/game/category/attribute/list',
    '/game/list',
    '/banner/list',
    '/page/item',
    '/promotion/list',
    '/static-content/list',
  ];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Cache only GET request

    if (req.method !== 'GET' || !this._allowedUrls.some((url) => req.url.includes(url))) {
      return next.handle(req);
    }

    // Cerate new cache key
    const storeKey = makeStateKey<any>(this._makeCacheKey(req));

    // Get data from cache in the browser
    if (this._platform.isBrowser) {
      const cachedResponse = this._transferState.get(storeKey, null);
      if (cachedResponse) {
        // Delete key from Transfer State after using
        this._transferState.remove(storeKey);
        // Returnun cached response
        return of(
          new HttpResponse({
            body: cachedResponse.body,
            headers: cachedResponse.headers ? cachedResponse.headers : undefined,
            status: cachedResponse.status,
            statusText: cachedResponse.statusText,
            url: cachedResponse.url || req.url,
          }),
        );
      }
    }

    // Check on server cache, if emptu - do request
    return next.handle(req).pipe(
      tap((event) => {
        // On server save data to Transfer State
        if (!this._platform.isBrowser && event instanceof HttpResponse) {
          this._transferState.set(storeKey, {
            body: event.body,
            headers: {},
            status: event.status,
            statusText: event.statusText,
            url: event.url,
          });
        }
      }),
    );
  }

  /**
   * Create cache key
   */
  private _makeCacheKey(req: HttpRequest<any>): string {
    return `http-cache:${this._removeCacheVersionParam(req.urlWithParams.replace('http:', 'https:'))}`;
  }

  private _removeCacheVersionParam(url: string): string {
    const [pathAndQuery, hash] = url.split('#');
    const [path, query] = pathAndQuery.split('?');

    if (!query) {
      return url;
    }

    const filteredQuery = query
      .split('&')
      .filter((param) => param.split('=')[0] !== 'bcv')
      .join('&');

    return `${path}${filteredQuery ? `?${filteredQuery}` : ''}${hash ? `#${hash}` : ''}`;
  }
}
